import { Button, DatePicker, Form, Input, Select } from "antd";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../app/store";
import DateIcon from "../../assets/icons/DateIcon";
import SelectDropdownLgIcon from "../../assets/icons/SelectDropdownLgIcon";
import {
  dateFormat,
  dbDateFormat,
  genderList,
} from "../../constants/constants";
import { useUpdateCompleteProfileMutation } from "../../features/auth/authApi";
import { setSuccessSignup } from "../../features/auth/authSlice";
import { useGetDepartmentsQuery } from "../../features/department/departmentApi";
import { useGetDesignationsQuery } from "../../features/designation/designationApi";
import useError from "../../hooks/useError";
import useModal from "../../hooks/useModal";
import { formatDate } from "../../lib/time";
import { ModalComponentProps } from "../../types";
import {
  selectArrayOption,
  selectSearchOption,
  selectStringOption,
} from "../../utils/utils";
import { CheckedData } from "../common/sidebar/SidebarContent";
import CustomModal from "../ui/CustomModal";
import CongratulationsProfileComplete from "./CongratulationsProfileComplete";

export default function AddInfoModalAfterLogin({
  handleClose,
  data,
  ...rest
}: ModalComponentProps<CheckedData>) {
  const [form] = Form.useForm();
  const dispatch = useAppDispatch();
  const [departmentId, setDepartmentId] = useState<number | undefined>(
    undefined
  );
  const {
    open: openSignupSuccess,
    handleClose: handleSignupClose,
    handleOpen: handleSignupOpen,
  } = useModal<undefined, "SUCCESS">(reRender);
  const { user, isSuccessSignup } = useAppSelector((state) => state.auth);
  const {
    data: departmentData,
    isError: isDepartmentError,
    error: departmentError,
  } = useGetDepartmentsQuery({ params: { limit: 0, skip: 0 } });
  const {
    data: designationData,
    isError: isDesignationError,
    error: designationError,
  } = useGetDesignationsQuery(
    {
      params: {
        limit: 0,
        skip: 0,
        ...(departmentId
          ? { business_department_id: departmentId?.toString() || "" }
          : {}),
      },
    },
    {
      skip: !departmentId,
    }
  );

  const [updateCompleteProfile, { isLoading, isError, isSuccess, error }] =
    useUpdateCompleteProfileMutation();

  // error toast message
  useError(isDepartmentError, departmentError, "Get department fail");
  useError(isDesignationError, designationError, "Get designation fail");
  useError(isError, error, "Profile update fail");

  const onFinish = (values: any) => {
    const { join_date, department_id, ...rest } = values;
    const newData = {
      ...rest,
      department: departmentData?.data?.departments?.find(
        (department) => department?.id === department_id
      )?.name,
      department_id,
      join_date: formatDate(join_date, dbDateFormat),
      profile_id: user?.data?.userData?.member?.profile_id,
      email: user?.data?.email,
    };

    if (user?.data?.userData?.business_member_id)
      return updateCompleteProfile({
        body: newData,
        id: user?.data?.userData?.business_member_id?.toString(),
      });
  };

  useEffect(() => {
    if (Object.keys(data || {})) {
      form.setFieldsValue({
        name: data?.name,
        gender: data?.gender,
        department_id: data?.department_id,
        business_role_id: data?.business_role_id,
        join_date: data?.joiningDate ? dayjs(data?.joiningDate) : undefined,
      });

      setDepartmentId(data?.department_id);
    }
  }, [data, form]);

  useEffect(() => {
    if (isSuccess) {
      handleClose();
    }
  }, [handleClose, isSuccess]);

  useEffect(() => {
    if (isSuccessSignup) {
      handleSignupOpen({
        type: "SUCCESS",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccessSignup]);

  // remove success message in redux
  function reRender() {
    dispatch(setSuccessSignup(false));
  }

  return (
    <CustomModal
      title="Fill up your information"
      {...rest}
      handleClose={handleClose}
      width={460}
      footer={null}
      disableCloseButton={!isSuccess}
    >
      {openSignupSuccess?.type === "SUCCESS" && (
        <CongratulationsProfileComplete
          open={openSignupSuccess.open}
          handleClose={handleSignupClose}
        />
      )}
      <Form
        className="custom-form"
        layout="vertical"
        requiredMark="optional"
        size="large"
        onFinish={onFinish}
        form={form}
      >
        <Form.Item
          name="name"
          label="Name"
          className="mb-4"
          rules={[
            {
              required: true,
              message: "Please enter name",
            },
          ]}
        >
          <Input size="large" placeholder="Enter your name" disabled />
        </Form.Item>
        <Form.Item
          name="gender"
          label="Gender"
          className="mb-4"
          rules={[
            {
              required: true,
              message: "Please select a gender",
            },
          ]}
        >
          <Select
            placeholder="Select"
            size="large"
            allowClear
            showSearch
            filterOption={selectSearchOption}
            options={selectStringOption(genderList)}
            suffixIcon={<SelectDropdownLgIcon />}
          />
        </Form.Item>

        <Form.Item
          name="department_id"
          label="Department"
          className="mb-4"
          rules={[
            {
              required: true,
              message: "Please select department",
            },
          ]}
        >
          <Select
            placeholder="Select"
            size="large"
            allowClear
            showSearch
            filterOption={selectSearchOption}
            options={selectArrayOption(
              departmentData?.data?.departments || [],
              "name",
              "id"
            )}
            suffixIcon={<SelectDropdownLgIcon />}
            onChange={(val) => setDepartmentId(val)}
          />
        </Form.Item>
        <Form.Item
          name="business_role_id"
          label="Designation"
          className="mb-4"
          rules={[
            {
              required: true,
              message: "Please select designation",
            },
          ]}
        >
          <Select
            placeholder="Select"
            size="large"
            allowClear
            showSearch
            filterOption={selectSearchOption}
            options={selectArrayOption(
              designationData?.data?.roles || [],
              "name",
              "id"
            )}
            suffixIcon={<SelectDropdownLgIcon />}
            disabled={!departmentId}
          />
        </Form.Item>
        <Form.Item
          name="join_date"
          label="Joining Date"
          className="mb-4"
          rules={[
            {
              required: true,
              message: "Please select joining date",
            },
          ]}
        >
          <DatePicker
            // defaultValue={dayjs(formatDate(), dateFormat)}
            format={dateFormat}
            suffixIcon={<DateIcon width={18} height={20} />}
            style={{ width: "100%" }}
            size="large"
          />
        </Form.Item>

        <div className="mt-6">
          <Button
            className="modal-button"
            type="primary"
            htmlType="submit"
            size="middle"
            loading={isLoading}
          >
            Submit
          </Button>
        </div>
      </Form>
    </CustomModal>
  );
}
